import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  Divider,
  Autocomplete,
  TextField,
  Chip,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import CloseIcon from "@mui/icons-material/Close";
import { postProject, updateProject, getProjectTypes } from "../../services/project.service";
import { getEmployee } from "../../services/employee.service";
import { ToastError, ToastSuccess } from "../../utils/Toaster/Toaster";
import { encryptData } from "../../utils/cryptoUtils";
import { getUser } from "../../services/user.service";

const AddProject = (props) => {
  const { setOpenDrawer, formType, selectedData, GetData } = props;
  const disabled = formType === "view";
  const formRef = useRef(null);
  const [employees, setEmployees] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);

  const [initialValues, setInitialValues] = useState({
    name: "",
    shortName: "",
    title: "",
    description: "",
    projectLead: [],
    projectType: [],
    payable: false,
  });

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    shortName: Yup.string().required("Short Name is required"),
    title: Yup.string().required("Title is required"),
    description: Yup.string().required("Description is required"),
    projectLead: Yup.array()
      .min(1, "At least one project lead is required")
      .required("Project lead is required"),
    projectType: Yup.array()
      .min(1, "At least one project type is required")
      .required("Project type is required"),
  });

  const fetchEmployees = async () => {
    try {
      const res = await getUser({ isEnabled: true });
      if (res?.data) {
        setEmployees(res.data.data);
      }
    } catch (error) {
      ToastError(error?.data?.message || "Failed to fetch employees");
    }
  };

  const fetchProjectTypes = async () => {
    try {
      const res = await getProjectTypes({ isEnabled: true });
      if (res?.data) {
        setProjectTypes(res.data);
      }
    } catch (error) {
      ToastError(error?.data?.message || "Failed to fetch project types");
    }
  };

  useEffect(() => {
    fetchEmployees();
    fetchProjectTypes();
  }, []);

  useEffect(() => {
    if ((formType === "edit" || formType === "view") && selectedData) {
      setInitialValues({
        name: selectedData.name || "",
        shortName: selectedData.shortName || "",
        title: selectedData.title || "",
        description: selectedData.description || "",
        projectLead:
          selectedData.projectLead?.map((lead) =>
            typeof lead === "object" ? lead._id : lead
          ) || [],
        projectType:
          selectedData.projectType?.map((type) =>
            typeof type === "object" ? type._id : type
          ) || [],
        payable: selectedData.payable || false,
      });
    }
  }, [formType, selectedData]);

  const onSubmit = async (values) => {
    try {
      const encryptedValues = {
        name: encryptData(values.name),
        shortName: encryptData(values.shortName),
        title: encryptData(values.title),
        description: encryptData(values.description),
      };

      const payload = {
        ...encryptedValues,
        projectLead: values.projectLead,
        projectType: values.projectType,
        payable: values.payable,
      };

      const apiMethod =
        formType === "edit"
          ? updateProject(selectedData._id, payload)
          : postProject(payload);
      const res = await apiMethod;
      ToastSuccess(res.message);
      setOpenDrawer(false);
      GetData();
    } catch (error) {
      ToastError(error?.data?.message);
    }
  };

  return (
    <div className="addRenderingPopup">
      <Box className="addContainerHeader">
        <Typography className="headerTitle">
          {formType === "edit" ? "Edit" : formType === "view" ? "View" : "Add"}{" "}
          Project
        </Typography>
        <CloseIcon
          sx={{ fontSize: "10px" }}
          className="Close-Icon"
          onClick={() => setOpenDrawer(false)}
        />
      </Box>
      <Divider sx={{ opacity: 1 }} />
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        innerRef={formRef}
      >
        {({ touched, errors, values, setFieldValue }) => (
          <Form>
            <Box className="addContainerCenter" mb={1}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} mt={1}>
                  <Typography className="inputLabel">
                    Name <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <Field
                    name="name"
                    className="InputField"
                    placeholder="Enter Name"
                    disabled={disabled}
                  />
                  <div className="form-error">
                    {touched.name && errors.name && errors.name}
                  </div>
                </Grid>
                <Grid item xs={12} sm={6} mt={1}>
                  <Typography className="inputLabel">
                    Short Name <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <Field
                    name="shortName"
                    className="InputField"
                    placeholder="Enter Short Name"
                    disabled={disabled}
                  />
                  <div className="form-error">
                    {touched.shortName && errors.shortName && errors.shortName}
                  </div>
                </Grid>
                <Grid item xs={12} sm={6} mt={1}>
                  <Typography className="inputLabel">
                    Title <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <Field
                    name="title"
                    className="InputField"
                    placeholder="Enter Title"
                    disabled={disabled}
                  />
                  <div className="form-error">
                    {touched.title && errors.title && errors.title}
                  </div>
                </Grid>
                <Grid item xs={12} sm={6} mt={1}>
                  <Typography className="inputLabel">
                    Description <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <Field
                    name="description"
                    className="InputField"
                    placeholder="Enter Description"
                    disabled={disabled}
                  />
                  <div className="form-error">
                    {touched.description &&
                      errors.description &&
                      errors.description}
                  </div>
                </Grid>
                <Grid item xs={12} sm={6} mt={1}>
                  <div className="wsh-form-control">
                    <Typography className="inputLabel">
                      Project Type <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Autocomplete
                      multiple
                      options={projectTypes || []}
                      getOptionLabel={(option) => option.name || ""}
                      value={
                        projectTypes?.filter((type) =>
                          values.projectType.includes(type._id)
                        ) || []
                      }
                      onChange={(e, newValue) => {
                        setFieldValue(
                          "projectType",
                          newValue.map((item) => item._id)
                        );
                      }}
                      disabled={disabled}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          focused={false}
                          placeholder="Select Project Type"
                          className="AutoComplete_InputBox"
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            label={option.name}
                            {...getTagProps({ index })}
                            disabled={disabled}
                          />
                        ))
                      }
                    />
                    <div className="form-error">
                      {touched.projectType &&
                        errors.projectType &&
                        errors.projectType}
                    </div>
                  </div>
                </Grid>
                <Grid item xs={12} sm={6} mt={1} display="flex" alignItems="center">
                  <div className="wsh-form-control" style={{ display: "flex", alignItems: "center", marginTop: "25px" }}>
                    <label style={{ marginRight: "10px", fontSize: "14px", fontWeight: "500" }}>Payable</label>
                    <Field type="checkbox" name="payable" disabled={disabled} style={{ width: "20px", height: "20px" }} />
                  </div>
                </Grid>
                <Grid item xs={12} sm={12} mt={1}>
                  <div className="wsh-form-control">
                    <Typography className="inputLabel">
                      Project Lead <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Autocomplete
                      multiple
                      options={employees}
                      getOptionLabel={(option) => option.name || ""}
                      value={employees.filter((emp) =>
                        values.projectLead.includes(emp._id)
                      )}
                      onChange={(e, newValue) => {
                        setFieldValue(
                          "projectLead",
                          newValue.map((item) => item._id)
                        );
                      }}
                      disabled={disabled}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          focused={false}
                          placeholder="Select Project Lead"
                          className="AutoComplete_InputBox"
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            label={option.name}
                            {...getTagProps({ index })}
                            disabled={disabled}
                          />
                        ))
                      }
                    />
                    <div className="form-error">
                      {touched.projectLead &&
                        errors.projectLead &&
                        errors.projectLead}
                    </div>
                  </div>
                </Grid>
              </Grid>
            </Box>
            <Divider sx={{ opacity: 1 }} />
            <Grid className="addboxFooter">
              <Button
                className="cancelButton"
                onClick={() => setOpenDrawer(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="saveButton"
                sx={{ display: disabled ? "none" : "flex" }}
              >
                Save
              </Button>
            </Grid>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default AddProject;